import React, { useState, useEffect } from "react";
import { getOpenSourceAdmin, createOpenSource, updateOpenSource, deleteOpenSource } from "../../../APIClient/apiClient";
import Toast from "../../../component/Toast";
import "../../../style/admin/AnnouncementManager.css";

const OpenSourceManager = () => {
  const [openSources, setOpenSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ message: "", type: "success" });
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [activeTab, setActiveTab] = useState("en");
  const [formData, setFormData] = useState({
    en: { name: "", release_date_text: "", notes: "", repository_text: "" },
    vi: { name: "", release_date_text: "", notes: "", repository_text: "" },
    technology: "",
    repository_url: "",
    contributor: "",
    sort_order: 0,
    is_active: true,
  });

  useEffect(() => {
    fetchOpenSources();
  }, []);

  const fetchOpenSources = async () => {
    setLoading(true);
    try {
      const data = await getOpenSourceAdmin();
      setOpenSources(data);
    } catch (err) {
      setToast({ message: "Failed to load open source projects", type: "error" });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (language, field, value) => {
    setFormData({
      ...formData,
      [language]: {
        ...formData[language],
        [field]: value,
      },
    });
  };

  const handleMetaChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        translations: {
          en: formData.en,
          vi: formData.vi,
        },
        technology: formData.technology,
        repository_url: formData.repository_url,
        contributor: formData.contributor,
        sort_order: formData.sort_order,
        is_active: formData.is_active,
      };

      if (editingId) {
        await updateOpenSource(editingId, payload);
      } else {
        await createOpenSource(payload);
      }
      fetchOpenSources();
      resetForm();
      setToast({ message: "Open source project saved successfully", type: "success" });
    } catch (err) {
      setToast({ message: err.message || "Failed to save open source project", type: "error" });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this open source project?")) {
      try {
        await deleteOpenSource(id);
        fetchOpenSources();
        setToast({ message: "Open source project deleted successfully", type: "success" });
      } catch (err) {
        setToast({ message: "Failed to delete open source project", type: "error" });
      }
    }
  };

  const handleEdit = (openSource) => {
    setFormData({
      en: {
        name: openSource.translations.en?.name || "",
        release_date_text: openSource.translations.en?.release_date_text || "",
        notes: openSource.translations.en?.notes || "",
        repository_text: openSource.translations.en?.repository_text || "",
      },
      vi: {
        name: openSource.translations.vi?.name || "",
        release_date_text: openSource.translations.vi?.release_date_text || "",
        notes: openSource.translations.vi?.notes || "",
        repository_text: openSource.translations.vi?.repository_text || "",
      },
      technology: openSource.technology || "",
      repository_url: openSource.repository_url || "",
      contributor: openSource.contributor || "",
      sort_order: openSource.sort_order,
      is_active: openSource.is_active,
    });
    setEditingId(openSource.id);
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      en: { name: "", release_date_text: "", notes: "", repository_text: "" },
      vi: { name: "", release_date_text: "", notes: "", repository_text: "" },
      technology: "",
      repository_url: "",
      contributor: "",
      sort_order: 0,
      is_active: true,
    });
    setEditingId(null);
    setShowForm(false);
    setActiveTab("en");
  };

  if (loading) return <div className="admin-manager-loading">Loading open source projects...</div>;

  return (
    <div className="admin-manager">
      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: "", type: "success" })} />

      <div className="admin-manager-header">
        <button className="admin-manager-btn-primary" onClick={() => (showForm ? resetForm() : setShowForm(true))}>
          {showForm ? "Cancel" : "Add New Open Source Project"}
        </button>
      </div>

      {showForm && (
        <div className="admin-manager-form">
          <h3>{editingId ? "Edit Open Source Project" : "Create New Open Source Project"}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-tabs">
              <button type="button" className={activeTab === "en" ? "active" : ""} onClick={() => setActiveTab("en")}>
                English
              </button>
              <button type="button" className={activeTab === "vi" ? "active" : ""} onClick={() => setActiveTab("vi")}>
                Vietnamese
              </button>
            </div>

            {activeTab === "en" && (
              <div className="form-section">
                <div className="form-group">
                  <label>Name (EN) *</label>
                  <input
                    type="text"
                    value={formData.en.name}
                    onChange={(e) => handleInputChange("en", "name", e.target.value)}
                    placeholder="English name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Release Date Text (EN)</label>
                  <input
                    type="text"
                    value={formData.en.release_date_text}
                    onChange={(e) => handleInputChange("en", "release_date_text", e.target.value)}
                    placeholder="e.g., Released 2024"
                  />
                </div>
                <div className="form-group">
                  <label>Notes (EN)</label>
                  <textarea
                    value={formData.en.notes}
                    onChange={(e) => handleInputChange("en", "notes", e.target.value)}
                    placeholder="English notes"
                  />
                </div>
                <div className="form-group">
                  <label>Repository Text (EN)</label>
                  <input
                    type="text"
                    value={formData.en.repository_text}
                    onChange={(e) => handleInputChange("en", "repository_text", e.target.value)}
                    placeholder="e.g., View on GitHub"
                  />
                </div>
              </div>
            )}

            {activeTab === "vi" && (
              <div className="form-section">
                <div className="form-group">
                  <label>Name (VI) *</label>
                  <input
                    type="text"
                    value={formData.vi.name}
                    onChange={(e) => handleInputChange("vi", "name", e.target.value)}
                    placeholder="Vietnamese name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Release Date Text (VI)</label>
                  <input
                    type="text"
                    value={formData.vi.release_date_text}
                    onChange={(e) => handleInputChange("vi", "release_date_text", e.target.value)}
                    placeholder="e.g., Phát hành 2024"
                  />
                </div>
                <div className="form-group">
                  <label>Notes (VI)</label>
                  <textarea
                    value={formData.vi.notes}
                    onChange={(e) => handleInputChange("vi", "notes", e.target.value)}
                    placeholder="Vietnamese notes"
                  />
                </div>
                <div className="form-group">
                  <label>Repository Text (VI)</label>
                  <input
                    type="text"
                    value={formData.vi.repository_text}
                    onChange={(e) => handleInputChange("vi", "repository_text", e.target.value)}
                    placeholder="e.g., Xem trên GitHub"
                  />
                </div>
              </div>
            )}

            <div className="form-section">
              <h4>General Information</h4>
              <div className="form-group">
                <label>Technology</label>
                <input
                  type="text"
                  value={formData.technology}
                  onChange={(e) => handleMetaChange("technology", e.target.value)}
                  placeholder="e.g., Python, JavaScript"
                />
              </div>
              <div className="form-group">
                <label>Repository URL</label>
                <input
                  type="text"
                  value={formData.repository_url}
                  onChange={(e) => handleMetaChange("repository_url", e.target.value)}
                  placeholder="https://github.com/..."
                />
              </div>
              <div className="form-group">
                <label>Contributor</label>
                <input
                  type="text"
                  value={formData.contributor}
                  onChange={(e) => handleMetaChange("contributor", e.target.value)}
                  placeholder="Contributor name"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Sort Order</label>
                <input type="number" value={formData.sort_order} onChange={(e) => handleMetaChange("sort_order", parseInt(e.target.value))} />
              </div>
              <div className="form-group">
                <label>Active</label>
                <input type="checkbox" checked={formData.is_active} onChange={(e) => handleMetaChange("is_active", e.target.checked)} />
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {editingId ? "Update" : "Create"} Open Source Project
              </button>
              <button type="button" className="btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="admin-manager-table">
        <table>
          <thead>
            <tr>
              <th>No.</th>
              <th>Name (EN)</th>
              <th>Technology</th>
              <th>Contributor</th>
              <th>Active</th>
              <th>Sort Order</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {openSources.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: "center", padding: "20px" }}>
                  No open source projects found
                </td>
              </tr>
            ) : (
              openSources.map((openSource, index) => (
                <tr key={openSource.id}>
                  <td>{index + 1}</td>
                  <td>{openSource.translations.en?.name || "N/A"}</td>
                  <td>{openSource.technology || "N/A"}</td>
                  <td>{openSource.contributor || "N/A"}</td>
                  <td>{openSource.is_active ? "Yes" : "No"}</td>
                  <td>{openSource.sort_order}</td>
                  <td className="action-buttons">
                    <button className="btn-edit" onClick={() => handleEdit(openSource)}>
                      Edit
                    </button>
                    <button className="btn-delete" onClick={() => handleDelete(openSource.id)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OpenSourceManager;
